import { useEffect, useState, type ReactNode } from 'react'
import { fetchSchema } from '../schema/fetchSchema'
import { getMapBundleSession } from '../mapDelivery/defaultMapBundleSession'
import { SchemaContext } from './schemaContext'
import type { SchemaData } from '../schema/types'

/**
 * Single owner of `fetchSchema()`. Mounted once at the AppShell level so
 * every consumer (StationPanel, slug-index, future StationDetails)
 * shares the same promise + same parsed-schema instance — no per-route
 * re-fetch and no race between the load and a route remount.
 *
 * The race the previous per-StationPanel fetch produced — back-button to
 * a previous station rendering a white screen because StationPanel hit
 * the "schema not loaded yet → resolved=null → navigate('/')" path —
 * is fixed by sharing one in-flight promise across all consumers.
 */
export function SchemaProvider({ children }: { children: ReactNode }): React.ReactElement {
  const [schema, setSchema] = useState<SchemaData | null>(null)

  useEffect(() => {
    let cancelled = false
    // TASK-1123: the AppShell is the map UI — it runs the map bundle launch
    // decision, so the schema and the canvas read the same bundle.
    void getMapBundleSession().begin('map')
    fetchSchema()
      .then((s) => {
        if (!cancelled) setSchema(s)
      })
      .catch((err) => {
        if (!cancelled) console.error('SchemaProvider: failed to load schema', err)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return <SchemaContext.Provider value={schema}>{children}</SchemaContext.Provider>
}
